// app/frontend/components/videos/VideoCard.tsx
import React from "react";
import {
  Card,
  Image,
  Group,
  ActionIcon,
  Stack,
  Box,
} from "@mantine/core";
import {
  IconHeart,
  IconBookmark,
  IconHeartFilled,
  IconBookmarkFilled,
} from "@tabler/icons-react";
import { L2KH6, L2KText, L2KBadge } from "../ui/Typography";
import type { L2KBadgeVariant } from "../ui/Typography/types";
import type { Video, UserVideo } from "../../types/video";

interface VideoCardProps {
  video: Video;
  userVideo?: UserVideo | null;
  onFavoriteToggle?: (videoId: number) => void;
  onBookmarkToggle?: (videoId: number) => void;
  onVideoClick?: (video: Video) => void;
  className?: string;
}

const VideoCard: React.FC<VideoCardProps> = ({
  video,
  userVideo,
  onFavoriteToggle,
  onBookmarkToggle,
  onVideoClick,
  className = "",
}) => {
  const favorited = !!userVideo?.favorited_at;
  const bookmarked = !!userVideo?.bookmarked_at;

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFavoriteToggle?.(video.id);
  };

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBookmarkToggle?.(video.id);
  };

  const handleCardClick = () => {
    onVideoClick?.(video);
  };

  const getSkillLevelVariant = (level: string): L2KBadgeVariant => {
    if (["beginner", "improver", "intermediate", "advanced"].includes(level)) {
      return level as L2KBadgeVariant;
    }
    return "muted";
  };

  return (
    <Card
      shadow="sm"
      padding="xs"
      radius="md"
      withBorder
      className={className}
      onClick={handleCardClick}
      style={{
        background: "rgba(255, 255, 255, 0.05)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s ease",
        height: "100%",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.5)";
        e.currentTarget.style.boxShadow = "0 8px 25px rgba(255, 152, 0, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <Card.Section>
        <Box
          style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}
        >
          <Image
            src={video.thumbnail_url}
            alt={video.title}
            fallbackSrc="https://picsum.photos/640/360?random=1"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      </Card.Section>

      <Stack gap="8px" mt="sm">
        <Group justify="space-between" align="flex-start">
          <L2KH6
            lineClamp={3}
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {video.title}
          </L2KH6>
          <Group gap={0} wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color={favorited ? "red" : "gray"}
              onClick={handleFavoriteClick}
              size="xs"
            >
              {favorited ? (
                <IconHeartFilled size={16} />
              ) : (
                <IconHeart size={16} />
              )}
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color={bookmarked ? "blue" : "gray"}
              onClick={handleBookmarkClick}
              size="xs"
            >
              {bookmarked ? (
                <IconBookmarkFilled size={16} />
              ) : (
                <IconBookmark size={16} />
              )}
            </ActionIcon>
          </Group>
        </Group>

        <L2KText size="xs" variant="secondary" lineClamp={2}>
          {video.description}
        </L2KText>

        <Group justify="space-between" mt={4}>
          <L2KBadge variant="muted">
            {video.duration}
          </L2KBadge>
          <L2KBadge variant={getSkillLevelVariant(video.skill_level)}>
            {video.skill_level}
          </L2KBadge>
        </Group>

        {/* Categories */}
        {video.categories && video.categories.length > 0 && (
          <Group gap={4} mt={4}>
            {video.categories.slice(0, 2).map((category) => (
              <L2KBadge key={category.id} variant="outline" size="sm">
                {category.name}
              </L2KBadge>
            ))}
            {video.categories.length > 2 && (
              <L2KBadge size="sm" variant="muted">
                +{video.categories.length - 2}
              </L2KBadge>
            )}
          </Group>
        )}

        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <Group gap={4} mt={4}>
            {video.tags.slice(0, 2).map((tag, index) => (
              <L2KBadge key={index} size="xs" variant="muted">
                {tag}
              </L2KBadge>
            ))}
            {video.tags.length > 2 && (
              <L2KBadge size="xs" variant="muted">
                +{video.tags.length - 2}
              </L2KBadge>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  );
};

export default VideoCard;
